import Image from "next/image";
import { useTranslations } from "next-intl";
import { ChangeEvent, useEffect, useMemo, useState } from "react";

import IconClose from "@/icons/icon-close";

import Icon from "./icon";
import styles from "./upload-images.module.scss";

type encodedImage = {
  base64: string;
  type: string;
};

type Props = {
  setImages: (images: encodedImage[]) => void;
};

const maxBytesSize = 5000000; // 5MB
const maxImages = 3;

const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(typeof reader.result == "string" ? reader.result.split(",")[1] : reader.result);
    reader.onerror = (error) => reject(error);
  });

export default function UploadImages({ setImages }: Props) {
  const [imagesArray, setImagesArray] = useState<File[]>([]);
  const [error, setError] = useState<string>();
  const t = useTranslations();

  const listenInputChanges = (e: ChangeEvent<HTMLInputElement>) => {
    // cleanup error
    setError(undefined);
    let filesArray = Array.from(e.target.files || []);
    if (filesArray.length > maxImages) {
      setError(`Max ${maxImages} images are allowed`);
      filesArray.splice(3);
    }
    filesArray = filesArray.filter((file) => {
      if (!file.type.match("image.*")) {
        setError(`Only images are allowed`);
        return false;
      }
      if (file.size >= maxBytesSize) {
        setError(`Max allowed size is ${maxBytesSize / 1000000} MB per image`);
        return false;
      }
      return true;
    });
    setImagesArray(filesArray);
  };

  const removeImg = (image: File) => {
    setImagesArray(imagesArray.filter((img) => img != image));
  };

  // on imagesArray update call setImages from props
  useEffect(() => {
    async function encodeImgs() {
      return await Promise.all(
        imagesArray.map(async (img) => {
          let imgEncoded = (await toBase64(img)) as string;
          return { base64: imgEncoded, type: img.type };
        })
      );
    }
    encodeImgs().then((data) => setImages(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesArray]);

  const previewBox = useMemo(() => {
    return (
      <output className={styles.previewOutput}>
        {imagesArray.map((image, i) => (
          <div key={i} className={styles.imagePreview}>
            <Image src={URL.createObjectURL(image)} width={80} height={80} alt={image.name} />
            <button onClick={() => removeImg(image)}>
              <Icon icon={IconClose} width={12} height={12} className={styles.iconRemove} />
            </button>
          </div>
        ))}
      </output>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesArray]);

  return (
    <div className={styles.uploadBox}>
      <label className={styles.labelUpload}>
        <p>{t("generic.attachPhotosOfProduct")}</p>
        <div className={styles.fakeButton}>
          {t("generic.uploadPhotos")}
          <input type="file" multiple accept="image/*" onChange={listenInputChanges} />
        </div>
      </label>
      {imagesArray.length > 0 && previewBox}
      {error && <p className={styles.errorMessage}>{error}</p>}
    </div>
  );
}
